/**
 * 放送コンテンツ編集 UI (`broadcastContents/`) で共有する定数。
 *
 * コンポーネントファイルから定数を export すると ESLint の
 * `react-refresh/only-export-components` に抵触するため、定数はこのファイルに集約する。
 * 各定義の内容・コメントは分割前の `components/BroadcastContentsEditor.tsx` からの移設で、
 * 値は一切変更していない。
 */
import type {
  BroadcastConditionOperator,
  BroadcastConditionType,
  BroadcastContent,
  ConnectingDelayJudgementMode,
  FreeTextInputMode,
} from '../../api/types';

/**
 * `PlaybackRateField` で使用するプリセット倍率。
 *
 * ユーザ調査 (聞き取り検証) の結果に基づき、実用範囲として 7 段階を用意する:
 * `0.5 / 0.75 / 0.85 / 1.0 / 1.15 / 1.25 / 1.5`。
 * 中央の 1.0 は等倍再生 (未指定と同義) で、両端の 0.5 / 1.5 は「聞き取りにくい
 * 駅名を丁寧に読み上げ」「短い注意喚起を素早く読み上げ」といった強めの調整用途。
 * それでも足りない場合は自由入力欄で 0.5〜2.0 の任意値を指定できる。
 */
export const PLAYBACK_RATE_PRESETS: ReadonlyArray<number> = [0.5, 0.75, 0.85, 1.0, 1.15, 1.25, 1.5];

/** playback_rate の許容範囲下限 (0.5 未満はサーバ側で 400 を返す)。 */
export const PLAYBACK_RATE_MIN = 0.5;
/** playback_rate の許容範囲上限 (2.0 超過はサーバ側で 400 を返す)。 */
export const PLAYBACK_RATE_MAX = 2.0;
/** playback_rate 未設定時のデフォルト値 (等倍再生)。 */
export const PLAYBACK_RATE_DEFAULT = 1.0;

/**
 * 音声プレビューの「サンプル条件」で使う既定の遅延秒数 (Issue #439)。
 *
 * 7 分 (= 420 秒) を採用する。`placeholders.ts` の `sample` 値
 * (`{delay_minutes_1}` = 7 / `{delay_minutes_5}` = 5 / `{delay_minutes_10}` = 0) と
 * 同じシナリオで、1 分 / 5 分 / 10 分すべての粒度で挙動の違いが分かる値になっている。
 *
 * ⚠️ サーバ側の既定値 (`lambda/common/preview_render.py` の
 * `DEFAULT_SAMPLE_DELAY_SECONDS` = 300) とは意図的に別管理としている。
 * フロントは必ず `sample_context.delay_seconds` を明示送信するため実際には
 * 本定数が使われ、サーバ側の値は「フロント以外の呼び出し元 (curl / 自動テスト) が
 * 省略したとき」のフォールバックとして機能する。
 */
export const PLACEHOLDER_SAMPLE_DELAY_SECONDS = 420;

/**
 * 音声プレビュー UI の `data-testid` プレフィックス (Issue #439)。
 *
 * `preview-button-<suffix>` / `preview-audio-<suffix>` のように、コンテンツ index と
 * フィールド名から一意に決まる規則にすることで、E2E / AI エージェントが安定して
 * 要素を掴めるようにする。
 *
 * 既存の `ssml-preview-audio` (任意テキスト放送のプレビュー) は E2E 互換のため
 * そのまま残す (`AudioPreviewField` の `legacyTestId` で指定する)。
 */
export const PREVIEW_BUTTON_TESTID_PREFIX = 'preview-button';
export const PREVIEW_AUDIO_TESTID_PREFIX = 'preview-audio';

/** 接続線遅延の判定モード選択肢 (フロント表示ラベル) */
export const CONNECTING_DELAY_MODE_META: ReadonlyArray<{
  mode: ConnectingDelayJudgementMode;
  label: string;
}> = [
  { mode: 'whole_line', label: '接続線全体の遅延' },
  { mode: 'section_scheduled', label: '区間・在線便の遅延' },
  { mode: 'transfer_aware', label: '乗り換え列車の遅延' },
];

export const FREE_TEXT_MODE_META: ReadonlyArray<{ mode: FreeTextInputMode; label: string }> = [
  { mode: 'text', label: '通常テキスト' },
  { mode: 'ssml', label: 'SSML' },
  { mode: 'audio', label: '音声ファイル' },
];

/**
 * 入力方法ラジオボタンの data-testid。
 *
 * E2E / AI エージェントが「通常テキスト」「SSML」「音声ファイル」のラジオを
 * 安定して選択できるようにするため、各ラジオに明示的な data-testid を付与する。
 *
 * ※ 画面ラベルは「通常テキスト」だが、value 属性は FreeTextInputMode のキー
 *   ("text" / "ssml" / "audio") であり、"plain" のような別名ではない点に注意。
 *   AI エージェントが画面ラベルから value を推測して `value="plain"` 等を
 *   投入する事故を避ける目的でも、testid 経由でのクリックを推奨する。
 */
export const FREE_TEXT_MODE_TESTID: Record<FreeTextInputMode, string> = {
  text: 'content-free-text-mode-text',
  ssml: 'content-free-text-mode-ssml',
  audio: 'content-free-text-mode-audio',
};

export const OPERATOR_META: ReadonlyArray<{
  operator: BroadcastConditionOperator;
  label: string;
}> = [
  { operator: 'gte', label: '以上' },
  { operator: 'lte', label: '以下' },
];

/**
 * 任意テキスト放送 (free_text) の放送条件の「条件種別」選択肢 (フロント表示ラベル)。
 *
 * `BroadcastCondition` は `condition_type` を discriminant とする判別可能ユニオンで、
 * 種別ごとに入力項目がまったく異なる。そのため種別セレクトの変更時は patch ではなく
 * **条件オブジェクトを丸ごと差し替える** (他種別のフィールドが DB / JSON に残らないよう
 * にする。`BroadcastConditionRow` の `handleModeChange` と同方針)。
 *
 * 並び順は「既定 (接続線遅延) を先頭」とし、`createDefaultBroadcastCondition()` が返す
 * 種別と一致させる。
 */
export const BROADCAST_CONDITION_TYPE_META: ReadonlyArray<{
  type: BroadcastConditionType;
  label: string;
}> = [
  { type: 'connecting_line_delay', label: '接続線遅延' },
  { type: 'arrival_track', label: '停車番線' },
];

/**
 * コンテンツ単位の列車種別 (`BroadcastContent.train_type`) の選択肢
 * (フロント表示ラベル)。
 *
 * - `'all'` (全列車) は **全線区で常に選択できる既定値** なので、線区マスタの
 *   `RouteMaster.train_types` には含めず (正源 `setting/routes-master.json` は
 *   `["rapid", "local"]` のみを持つ)、本 META 側に固定で 3 件目として持つ。
 *   → `train_types` 未定義の線区では「全列車」だけが選択肢になり、
 *     受入条件「快速・各駅は列車種別がある線区のみで表示・設定できる」を満たす。
 * - 並び順は「既定 (全列車) を先頭」とし、`<select>` の初期表示と既定挙動を揃える。
 * - 値は ControlPanelApi の `ALLOWED_TRAIN_TYPES`
 *   (`lambda/control-panel-api/index.py`) と必ず一致させること。
 */
export const TRAIN_TYPE_META = [
  { value: 'all', label: '全列車' },
  { value: 'rapid', label: '快速' },
  { value: 'local', label: '各駅停車' },
] as const satisfies ReadonlyArray<{
  value: NonNullable<BroadcastContent['train_type']>;
  label: string;
}>;

/**
 * `train_type` 未設定時のデフォルト値 (全列車)。
 *
 * サーバ側 (`lambda/control-panel-api/index.py` の `_TRAIN_TYPE_DEFAULT`) と対応し、
 * この値は「未指定と同義」として **`train_type` キーごと保存しない**
 * (既存 item と bit-perfect 互換を保つため)。
 */
export const TRAIN_TYPE_DEFAULT = 'all';

/**
 * `train_type` として許容される値の一覧 (JSON インポート時の検証用)。
 *
 * サーバ側は `ALLOWED_TRAIN_TYPES` 以外を 400 にするため、インポート経路でも
 * この 3 値以外はキーごと捨てて「保存できないデータ」を作らないようにする。
 */
export const ALLOWED_TRAIN_TYPES: ReadonlyArray<NonNullable<BroadcastContent['train_type']>> =
  TRAIN_TYPE_META.map((m) => m.value);
